import React, { useState } from "react";
import { Row, Col, FormGroup, Label, Input, Button, FormFeedback, Tooltip } from "reactstrap";
import { useTranslation } from "react-i18next";
import { Formik, Form as FormikForm, Field, ErrorMessage } from "formik";
import * as yup from "yup";
import { AddMedicineFormData } from "../medicineTypes";
import Select from "react-select";

const validationSchema = yup.object({
  name: yup.string().required("Medicine Name is required"),
  dosageForm: yup.string().required("Dosage Form is required"),
  medicineType: yup.string().required("Medicine Type is required"),
  prescriptionRequired: yup.boolean().required("Prescription Requirement is required"),
  alias: yup.string(),
  genericName: yup.string(),
  description: yup.string(),
  strength: yup.string(),
  routeOfAdministration: yup.string(),
  itemAlert: yup.boolean(),
  allowDue: yup.boolean(),
  generatePO: yup.boolean(),
  isActive: yup.boolean(),
  printable: yup.boolean(),
});

interface BasicInfoFormProps {
  formData: AddMedicineFormData;
  onNext: (values: AddMedicineFormData) => void;
  onSkip: () => void;
  onSkipAll: () => void;
  isUrdu: boolean;
}

export const BasicInfoForm: React.FC<BasicInfoFormProps> = ({
  formData,
  onNext,
  onSkip,
  onSkipAll,
  isUrdu,
}) => {
  const { t } = useTranslation();
  const [tooltipOpen, setTooltipOpen] = useState<{ [key: string]: boolean }>({});

  const toggleTooltip = (field: string) =>
    setTooltipOpen((prev) => ({ ...prev, [field]: !prev[field] }));

  const medicineTypes = [
    "TabletPack",
    "Syrup",
    "Injection",
    "Capsule",
    "Drops",
    "Ointment",
    "Cream",
    "Powder",
    "Inhaler",
    "Lotion",
    "Gel",
    "Suppository",
    "Patch",
    "Solution",
    "Spray",
    "Suspension",
    "Other",
  ];

  return (
    <Formik
      initialValues={{
        name: formData.name || "",
        dosageForm: formData.dosageForm || "",
        medicineType: formData.medicineType || "",
        prescriptionRequired: formData.prescriptionRequired || false,
        alias: formData.alias || "",
        genericName: formData.genericName || "",
        description: formData.description || "",
        strength: formData.strength || "",
        routeOfAdministration: formData.routeOfAdministration || "",
        itemAlert: formData.itemAlert || false,
        allowDue: formData.allowDue || false,
        generatePO: formData.generatePO || false,
        isActive: formData.isActive ?? true,
        printable: formData.printable ?? true,
      }}
      validationSchema={validationSchema}
      onSubmit={(values) => onNext({ ...formData, ...values })}
      enableReinitialize
    >
      {({ errors, touched, isValid, dirty }) => (
        <FormikForm dir={isUrdu ? "rtl" : "ltr"} className="p-3">
          <Row>
            <Col lg={6}>
              <FormGroup className="mb-3">
                <Label htmlFor="name" className={isUrdu ? "fw-bold" : ""}>
                  {t("Medicine Name (Brand Name)")} *{" "}
                  <span id="nameTooltip" className="text-info">?</span>
                </Label>
                <Tooltip
                  placement="top"
                  isOpen={tooltipOpen.name}
                  target="nameTooltip"
                  toggle={() => toggleTooltip("name")}
                >
                  {t("Enter the brand name of the medicine, e.g., Panadol.")}
                </Tooltip>
                <Field
                  name="name"
                  as={Input}
                  id="name"
                  placeholder={t("e.g., Panadol, Brufen")}
                  invalid={touched.name && !!errors.name}
                  style={{ textAlign: isUrdu ? "right" : "left" }}
                  className="form-control"
                />
                <ErrorMessage
                  name="name"
                  component={FormFeedback}
                  className={isUrdu ? "fw-bold" : ""}
                />
              </FormGroup>
            </Col>
            <Col lg={6}>
              <FormGroup className="mb-3">
                <Label htmlFor="alias" className={isUrdu ? "fw-bold" : ""}>
                  {t("Alias")} <span id="aliasTooltip" className="text-info">?</span>
                </Label>
                <Tooltip
                  placement="top"
                  isOpen={tooltipOpen.alias}
                  target="aliasTooltip"
                  toggle={() => toggleTooltip("alias")}
                >
                  {t("Alternative name or code for the medicine.")}
                </Tooltip>
                <Field
                  name="alias"
                  as={Input}
                  id="alias"
                  placeholder={t("e.g., Paracetamol")}
                  style={{ textAlign: isUrdu ? "right" : "left" }}
                  className="form-control"
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col lg={6}>
              <FormGroup className="mb-3">
                <Label htmlFor="genericName" className={isUrdu ? "fw-bold" : ""}>
                  {t("Generic Name")} <span id="genericNameTooltip" className="text-info">?</span>
                </Label>
                <Tooltip
                  placement="top"
                  isOpen={tooltipOpen.genericName}
                  target="genericNameTooltip"
                  toggle={() => toggleTooltip("genericName")}
                >
                  {t("Generic or active ingredient name, e.g., Ibuprofen.")}
                </Tooltip>
                <Field
                  name="genericName"
                  as={Input}
                  id="genericName"
                  placeholder={t("e.g., Ibuprofen")}
                  style={{ textAlign: isUrdu ? "right" : "left" }}
                  className="form-control"
                />
              </FormGroup>
            </Col>
            <Col lg={6}>
              <FormGroup className="mb-3">
                <Label htmlFor="dosageForm" className={isUrdu ? "fw-bold" : ""}>
                  {t("Dosage Form")} *{" "}
                  <span id="dosageFormTooltip" className="text-info">?</span>
                </Label>
                <Tooltip
                  placement="top"
                  isOpen={tooltipOpen.dosageForm}
                  target="dosageFormTooltip"
                  toggle={() => toggleTooltip("dosageForm")}
                >
                  {t("Specify the form of the medicine, e.g., Tablet, Syrup.")}
                </Tooltip>
                <Field
                  name="dosageForm"
                  as={Input}
                  id="dosageForm"
                  placeholder={t("e.g., Tablet, Syrup, Injection")}
                  invalid={touched.dosageForm && !!errors.dosageForm}
                  style={{ textAlign: isUrdu ? "right" : "left" }}
                  className="form-control"
                />
                <ErrorMessage
                  name="dosageForm"
                  component={FormFeedback}
                  className={isUrdu ? "fw-bold" : ""}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col lg={6}>
              <FormGroup className="mb-3">
                <Label htmlFor="medicineType" className={isUrdu ? "fw-bold" : ""}>
                  {t("Medicine Type")} *{" "}
                  <span id="medicineTypeTooltip" className="text-info">?</span>
                </Label>
                <Tooltip
                  placement="top"
                  isOpen={tooltipOpen.medicineType}
                  target="medicineTypeTooltip"
                  toggle={() => toggleTooltip("medicineType")}
                >
                  {t("Select the type of medicine, e.g., TabletPack, Syrup.")}
                </Tooltip>
                <Field name="medicineType">
                  {({ field, form }: any) => (
                    <div>
                      <Select
                        id="medicineType"
                        name="medicineType"
                        options={medicineTypes.map((type) => ({
                          value: type,
                          label: t(type),
                        }))}
                        value={
                          field.value
                            ? { value: field.value, label: t(field.value) }
                            : null
                        }
                        onChange={(option: any) => {
                          form.setFieldValue("medicineType", option ? option.value : "");
                        }}
                        placeholder={t("Select Medicine Type")}
                        isSearchable
                        classNamePrefix={
                          touched.medicineType && errors.medicineType ? "is-invalid" : ""
                        }
                        styles={{
                          control: (base) => ({
                            ...base,
                            textAlign: isUrdu ? "right" : "left",
                          }),
                        }}
                      />
                    </div>
                  )}
                </Field>
                <ErrorMessage
                  name="medicineType"
                  component={FormFeedback}
                  className={isUrdu ? "fw-bold" : ""}
                />
              </FormGroup>
            </Col>
            <Col lg={6}>
              <FormGroup className="mb-3">
                <Label htmlFor="strength" className={isUrdu ? "fw-bold" : ""}>
                  {t("Strength")} <span id="strengthTooltip" className="text-info">?</span>
                </Label>
                <Tooltip
                  placement="top"
                  isOpen={tooltipOpen.strength}
                  target="strengthTooltip"
                  toggle={() => toggleTooltip("strength")}
                >
                  {t("Dosage strength, e.g., 500mg, 10ml.")}
                </Tooltip>
                <Field
                  name="strength"
                  as={Input}
                  id="strength"
                  placeholder={t("e.g., 500mg")}
                  style={{ textAlign: isUrdu ? "right" : "left" }}
                  className="form-control"
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col lg={6}>
              <FormGroup className="mb-3">
                <Label htmlFor="routeOfAdministration" className={isUrdu ? "fw-bold" : ""}>
                  {t("Route of Administration")} <span id="routeTooltip" className="text-info">?</span>
                </Label>
                <Tooltip
                  placement="top"
                  isOpen={tooltipOpen.routeOfAdministration}
                  target="routeTooltip"
                  toggle={() => toggleTooltip("routeOfAdministration")}
                >
                  {t("Method of administration, e.g., Oral, Intravenous.")}
                </Tooltip>
                <Field
                  name="routeOfAdministration"
                  as={Input}
                  id="routeOfAdministration"
                  placeholder={t("e.g., Oral, Intravenous")}
                  style={{ textAlign: isUrdu ? "right" : "left" }}
                  className="form-control"
                />
              </FormGroup>
            </Col>
            <Col lg={6}>
              <FormGroup className="mb-3">
                <Label htmlFor="description" className={isUrdu ? "fw-bold" : ""}>
                  {t("Description")} <span id="descriptionTooltip" className="text-info">?</span>
                </Label>
                <Tooltip
                  placement="top"
                  isOpen={tooltipOpen.description}
                  target="descriptionTooltip"
                  toggle={() => toggleTooltip("description")}
                >
                  {t("Brief description of the medicine.")}
                </Tooltip>
                <Field
                  name="description"
                  as={Input}
                  type="textarea"
                  id="description"
                  placeholder={t("e.g., Pain reliever and fever reducer")}
                  style={{ textAlign: isUrdu ? "right" : "left" }}
                  className="form-control"
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col xs={4} sm={2}>
              <FormGroup className="mb-3">
                <Label className={isUrdu ? "fw-bold" : ""}>
                  <Field
                    type="checkbox"
                    name="prescriptionRequired"
                    id="prescriptionRequired"
                    className="form-check-input me-2"
                  />
                  {t("Prescription")} * <span id="prescriptionTooltip" className="text-info">?</span>
                </Label>
                <Tooltip
                  placement="top"
                  isOpen={tooltipOpen.prescriptionRequired}
                  target="prescriptionTooltip"
                  toggle={() => toggleTooltip("prescriptionRequired")}
                >
                  {t("Check if the medicine requires a prescription.")}
                </Tooltip>
              </FormGroup>
            </Col>
            <Col xs={4} sm={2}>
              <FormGroup className="mb-3">
                <Label className={isUrdu ? "fw-bold" : ""}>
                  <Field
                    type="checkbox"
                    name="itemAlert"
                    id="itemAlert"
                    className="form-check-input me-2"
                  />
                  {t("Item Alert")} <span id="itemAlertTooltip" className="text-info">?</span>
                </Label>
                <Tooltip
                  placement="top"
                  isOpen={tooltipOpen.itemAlert}
                  target="itemAlertTooltip"
                  toggle={() => toggleTooltip("itemAlert")}
                >
                  {t("Enable alerts for low stock or other issues.")}
                </Tooltip>
              </FormGroup>
            </Col>
            <Col xs={4} sm={2}>
              <FormGroup className="mb-3">
                <Label className={isUrdu ? "fw-bold" : ""}>
                  <Field
                    type="checkbox"
                    name="allowDue"
                    id="allowDue"
                    className="form-check-input me-2"
                  />
                  {t("Allow Due")} <span id="allowDueTooltip" className="text-info">?</span>
                </Label>
                <Tooltip
                  placement="top"
                  isOpen={tooltipOpen.allowDue}
                  target="allowDueTooltip"
                  toggle={() => toggleTooltip("allowDue")}
                >
                  {t("Allow sales on credit or due payment.")}
                </Tooltip>
              </FormGroup>
            </Col>
            <Col xs={4} sm={2}>
              <FormGroup className="mb-3">
                <Label className={isUrdu ? "fw-bold" : ""}>
                  <Field
                    type="checkbox"
                    name="generatePO"
                    id="generatePO"
                    className="form-check-input me-2"
                  />
                  {t("Generate PO")} <span id="generatePOTooltip" className="text-info">?</span>
                </Label>
                <Tooltip
                  placement="top"
                  isOpen={tooltipOpen.generatePO}
                  target="generatePOTooltip"
                  toggle={() => toggleTooltip("generatePO")}
                >
                  {t("Enable automatic purchase order generation.")}
                </Tooltip>
              </FormGroup>
            </Col>
            <Col xs={4} sm={2}>
              <FormGroup className="mb-3">
                <Label className={isUrdu ? "fw-bold" : ""}>
                  <Field
                    type="checkbox"
                    name="isActive"
                    id="isActive"
                    className="form-check-input me-2"
                  />
                  {t("Is Active")} <span id="isActiveTooltip" className="text-info">?</span>
                </Label>
                <Tooltip
                  placement="top"
                  isOpen={tooltipOpen.isActive}
                  target="isActiveTooltip"
                  toggle={() => toggleTooltip("isActive")}
                >
                  {t("Mark the medicine as active for sales.")}
                </Tooltip>
              </FormGroup>
            </Col>
            <Col xs={4} sm={2}>
              <FormGroup className="mb-3">
                <Label className={isUrdu ? "fw-bold" : ""}>
                  <Field
                    type="checkbox"
                    name="printable"
                    id="printable"
                    className="form-check-input me-2"
                  />
                  {t("Printable")} <span id="printableTooltip" className="text-info">?</span>
                </Label>
                <Tooltip
                  placement="top"
                  isOpen={tooltipOpen.printable}
                  target="printableTooltip"
                  toggle={() => toggleTooltip("printable")}
                >
                  {t("Allow printing on labels or receipts.")}
                </Tooltip>
              </FormGroup>
            </Col>
          </Row>
          <div className="d-flex justify-content-between mt-4">
            <Button color="secondary" onClick={onSkipAll} className={isUrdu ? "fw-bold" : ""}>
              {t("Skip All")}
            </Button>
            <Button
              type="submit"
              color="primary"
              disabled={!(isValid && dirty)}
              className={isUrdu ? "fw-bold" : ""}
            >
              {t("Next")}
            </Button>
          </div>
        </FormikForm>
      )}
    </Formik>
  );
};